import { useEffect, useState } from "react";
import DataTable, { Column } from "../../components/DataTable";
import { AssignedTeacher } from "../../types/classesTypes";
import classesApis from "../../api/classesApis";
import { useNavigate, useParams } from "react-router-dom";
import { formatRoleForDisplay } from "../../util/functions/UniversalFunctions";

interface Props {
  selectedTeachers: number[];
  onSelect: (ids: number[]) => void;
  refreshKey: number;
}

export default function TeachersTab({
  selectedTeachers,
  onSelect,
  refreshKey,
}: Props) {
  const { classId } = useParams<{ classId: string }>();
  const parsedClassId = Number(classId);
  const [assignedTeacher, setAssignedTeacher] = useState<AssignedTeacher[]>([]);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const fetchTeachers = async () => {
    setLoading(true);
    try {
      const res = await classesApis.fetchAssignedTeachers(parsedClassId);
      if (res?.data) setAssignedTeacher(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!parsedClassId) return;
    fetchTeachers();
  }, [parsedClassId, refreshKey]);

  const handleViewProfile = (staffId: number) => {
    navigate(`/staff/staff-profile/${staffId}`);
  };

  const toggleSelect = (staffId: number) => {
    if (selectedTeachers.includes(staffId)) {
      onSelect(selectedTeachers.filter((id) => id !== staffId));
    } else {
      onSelect([...selectedTeachers, staffId]);
    }
  };

  const toggleSelectAll = () => {
    if (selectedTeachers.length === assignedTeacher.length) {
      onSelect([]);
    } else {
      onSelect(assignedTeacher.map((t) => t.staffId));
    }
  };

  const columns: Column<AssignedTeacher>[] = [
    {
      label: (
        <input
          type="checkbox"
          className="w-3 h-3"
          checked={
            assignedTeacher.length > 0 &&
            selectedTeachers.length === assignedTeacher.length
          }
          onChange={toggleSelectAll}
        />
      ),
      accessor: "select",
      render: (item) => (
        <input
          type="checkbox"
          className="w-3 h-3"
          checked={selectedTeachers.includes(item.staffId)}
          onChange={() => toggleSelect(item.staffId)}
        />
      ),
    },
    {
      label: "SL NO.",
      accessor: "employeeCode",
    },
    {
      label: "Profile",
      accessor: "profile",
      render: (item) => (
        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary-600 text-white font-semibold">
          {item.staffName.charAt(0).toUpperCase()}
        </div>
      ),
    },
    {
      label: "Staff Name",
      accessor: "staffName",
      // searchable: true,
      render: (item) => (
        <div className="flex flex-col gap-1">
          <span className="font-bold text-sm text-neutral-400">
            {item.staffName}
          </span>

          {item.isClassTeacher && (
            <span className="inline-block w-fit px-2 py-[2px] text-xs font-semibold rounded-full bg-success-100 text-success-700">
              Class Teacher
            </span>
          )}
        </div>
      ),
    },
    {
      label: "Designation",
      accessor: "roleName",
      // searchable: true,
      render: (item) => (
        <p className="capitalize">{formatRoleForDisplay(item.roleName)}</p>
      ),
    },
    {
      label: "Action",
      accessor: "action",
      searchable: false,
      render: (item) => (
        <button
          onClick={() => handleViewProfile(item.staffId)}
          className="px-2 py-1 text-xs text-warning-700 bg-warning-50 rounded-[4px] font-medium"
        >
          View Profile
        </button>
      ),
    },
  ];

  return (
    <div className="bg-white px-6 py-5 rounded-lg">
      <DataTable
        columns={columns}
        data={assignedTeacher}
        isLoading={loading}
        rowClassName={(row) => (row.isClassTeacher ? "bg-success-50" : "")}
      />
    </div>
  );
}
